import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Headphones, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/site";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "products", label: "Products" },
  { id: "services", label: "Services" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "industries", label: "Industries" },
  { id: "contact", label: "Contact" },
];

export default function Navbar({ onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-navy/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <button
          data-testid="nav-logo-button"
          onClick={() => go("home")}
          aria-label="JAYTRIKA home"
          className="shrink-0"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-cyan-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            data-testid="nav-support-button"
            onClick={() => go("contact")}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 font-mono text-xs font-semibold tracking-wider text-cyan-300 transition-colors duration-200 hover:bg-cyan-400/20"
          >
            <Headphones size={14} />
            24/7 Support
          </button>
          <button
            data-testid="nav-get-quote-button"
            onClick={() => onQuote()}
            className="group inline-flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/30 transition-all duration-200 hover:bg-blue-500 hover:shadow-blue-500/40"
          >
            Get a Quote
            <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          className="rounded-lg border border-white/10 p-2 text-white lg:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/10 bg-navy/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-200 transition-colors hover:bg-white/5 hover:text-cyan-300"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-3 flex gap-3">
                <button
                  data-testid="nav-mobile-support-button"
                  onClick={() => go("contact")}
                  className="flex-1 rounded-full border border-cyan-400/30 px-4 py-3 text-center text-sm font-semibold text-cyan-300"
                >
                  24/7 Support
                </button>
                <button
                  data-testid="nav-mobile-quote-button"
                  onClick={() => {
                    setOpen(false);
                    onQuote();
                  }}
                  className="flex-1 rounded-full bg-electric px-4 py-3 text-center text-sm font-semibold text-white"
                >
                  Get a Quote
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
